import React, { useState } from "react";
import {
    Typography,
    Button,
    TextField,
    Box,
    InputAdornment,
    Tooltip,
    IconButton,
} from "@mui/material";
import { ReactComponent as ThumbsUp } from "../../../assets/img/thumbsUp.svg";
import Page from "components/ui/Page/Page";
import {
    FormContainer,
    inputStyles,
} from "components/ui/FormField/FormFieldContainer";
import FileDownloadDoneIcon from "@mui/icons-material/FileDownloadDone";
import AssignmentIcon from "@mui/icons-material/Assignment";

const TreatResult = ({ onContinue }) => {
    return (
        <Page
            heading="You’re not alone"
            subHeading="Based on your answers, you qualify for our Treat program, built to support people who have already been diagnosed with a heart condition. You’ll have tools and clinical support to help manage your heart health with confidence, right from home."
        >
            <FormContainer
                heading="What Happens Next?"
                subHeading="Create your KardiaComplete account and provide your shipping address, so we can send you your KardiaComplete kit."
                sx={{
                    width: "65%",
                }}
            >
                <Button variant="contained" onClick={onContinue}>
                    Continue
                </Button>
            </FormContainer>
        </Page>
    );
};

const PreventResult = ({ onContinue }) => {
    return (
        <Page
            heading="You’re in the right place"
            subHeading="Based on your answers, you qualify for our Prevent program, designed to help catch heart risk early and support healthier habits over time. Starting now gives you more visibility into your heart health and the chance to stay ahead of potential issues."
        >
            <FormContainer
                sx={{
                    width: "65%",
                }}
                heading="What Happens Next?"
                subHeading="Create your KardiaComplete account and provide your shipping address, so we can send you your KardiaComplete kit."
            >
                <Button variant="contained" onClick={onContinue}>
                    Continue
                </Button>
            </FormContainer>
        </Page>
    );
};

const LowRiskResult = ({ formData, onNoThanks }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(formData?.discountCode || "");
        setCopied(true);

        // Reset tooltip text after 3 seconds
        setTimeout(() => {
            setCopied(false);
        }, 3000);
    };

    return (
        <Page
            heading="Great News! You’re Low Risk"
            subHeading="Based on your answers, you don’t qualify for this program right now. If you’d still like to monitor your heart, you can purchase a Kardia device at a discounted rate using the discount code below."
        >
            <FormContainer
                sx={{
                    textAlign: "center",
                    width: "65%",
                }}
            >
                <TextField
                    readOnly
                    fullWidth
                    name="discountCode"
                    value={formData?.discountCode || ""}
                    sx={{
                        ...inputStyles(),
                        boxShadow: "0px 1px 3px 0px rgba(0, 0, 0, 0.12)",
                        outline: "none",
                        textAlignLast: "center",
                        "& .MuiOutlinedInput-notchedOutline": {
                            borderWidth: "0px",
                        },
                        "& :hover": {
                            cursor: "pointer",
                        },
                    }}
                    onClick={handleCopy}
                    InputProps={{
                        readOnly: true,
                        endAdornment: (
                            <InputAdornment position="end">
                                <Tooltip title={copied ? "Copied!" : "Copy to clipboard"}>
                                    <IconButton edge="end">
                                        {copied ? (
                                            <FileDownloadDoneIcon color="success" />
                                        ) : (
                                            <AssignmentIcon />
                                        )}
                                    </IconButton>
                                </Tooltip>
                            </InputAdornment>
                        ),
                    }}
                />
            </FormContainer>
            <Box>
                <ThumbsUp />
            </Box>
        </Page>
    );
};

/**
 * @description Component to display the program result based on the user's eligibility. It shows different content for Treat, Prevent, and Low Risk outcomes.
 * @param {*} props - The component props
 * @param {string} props.program - The program the user qualifies for ("TREAT", "PREVENT", or null for low risk)
 * @param {function} props.onContinue - Callback function to continue to the next step (address collection) for Treat and Prevent programs
 * @param {function} props.onNoThanks - Callback function to reset the flow for low risk users who may want to exit or start over
 * @param {string} props.discountCode - The discount code to display for low risk users
 * @returns {JSX.Element} The rendered component based on the program result
 */
const ProgramResult = ({ program, onContinue, onNoThanks, discountCode }) => {
    return (
        <>
            {program === "TREAT" && <TreatResult onContinue={onContinue} />}
            {program === "PREVENT" && <PreventResult onContinue={onContinue} />}
            {!program && (
                <LowRiskResult formData={{ discountCode }} onNoThanks={onNoThanks} />
            )}
        </>
    );
};

export default ProgramResult;
